import type { FunctionComponent } from 'react';
import classnames from 'classnames';

type HeadingProps = {
  level?: 1 | 2 | 3 | 4 | 5;
};

//  Returns the classname for each level.
const getClassName = (level: HeadingProps['level']): string => classnames(
  'fossa-text',
  'fossa-text__heading',
  `fossa-text__heading__h${level}`,
);

//  Map level prop to the proper html heading tag.
const HeadingMap: FunctionComponent[] = [
  ({ children }) => <h1 className={getClassName(1)}>{children}</h1>,
  ({ children }) => <h2 className={getClassName(2)}>{children}</h2>,
  ({ children }) => <h3 className={getClassName(3)}>{children}</h3>,
  ({ children }) => <h4 className={getClassName(4)}>{children}</h4>,
  ({ children }) => <h5 className={getClassName(5)}>{children}</h5>,
];

/**
 * A generic FOSSA Heading component, with support for h1-h3 tags.
 *
 * @param {HeadingProps} props - Heading props
 * @param {React.ReactChild} props.children - The text to display as a heading.
 * @param {1 | 2 | 3 | 4 | 5} [props.level] - The heading level (either 1, 2, 3, 4, 5).
 */
const Heading: FunctionComponent<HeadingProps> = ({ children, level = 3 }) => {
  const HeadingComponent = (HeadingMap[level - 1] || HeadingMap[2]) as FunctionComponent; // fallback to <h3>
  return (
    <HeadingComponent>
      {children}
    </HeadingComponent>
  );
};

export default Heading;
